/**
 * Resource-selector parsing for the Policy Studio form.
 *
 * The form takes selectors as text. Two shapes are accepted:
 *
 *   key:value pairs   `domain:finance, classification:confidential`
 *   JSON objects      `{"field": "domain", "op": "eq", "value": "finance"}`
 *
 * Both produce exactly the object the API stores, so a policy authored in the
 * console is byte-for-byte the policy authored through the SDK or the CLI.
 *
 * History: until 2026-09-25 the form stored `domain:finance` as
 * `{"field": "domain:finance", "op": "eq", "value": "domain:finance"}`. A field
 * the engine does not resolve matches no resource, so the policy silently
 * applied to nothing and the org's fail-closed default denied everything,
 * including resources the policy was written to allow (cold run, P0).
 */

export type SelectorOp = 'eq' | 'ne' | 'in' | 'contains' | 'gte' | 'lte';

export interface ResourceSelector {
  field: string;
  op: SelectorOp;
  value: string | string[];
  actions?: string[];
}

/** Mirrors `RESOURCE_CONDITION_FIELDS` in the backend policy schemas. */
export const SELECTOR_FIELDS = [
  'domain',
  'classification',
  'sensitivity',
  'labels',
  'encryption_mode',
  'connector_id',
  'source_connector_id',
  'owner_principal',
] as const;

const SELECTOR_OPS: readonly SelectorOp[] = ['eq', 'ne', 'in', 'contains', 'gte', 'lte'];

/** Closed vocabularies the backend enforces; anything else never matches. */
const VOCABULARY: Record<string, readonly string[]> = {
  classification: ['public', 'internal', 'confidential', 'restricted'],
  sensitivity: ['low', 'medium', 'high', 'critical'],
  encryption_mode: ['none', 'at_rest', 'envelope', 'full'],
};

/** Fields that hold a list on the resource; `key:value` on them means "contains". */
const LIST_FIELDS = new Set<string>(['labels']);

const FIELD_ALIASES: Record<string, string> = { label: 'labels', tag: 'labels', tags: 'labels' };

const KEYS_HINT = SELECTOR_FIELDS.join(', ');

export interface ParsedSelectors {
  selectors: ResourceSelector[];
  error: string | null;
}

function normalizeField(raw: string): string {
  const bare = raw.trim().toLowerCase().replace(/^resource\./, '');
  return FIELD_ALIASES[bare] ?? bare;
}

function checkVocabulary(field: string, value: unknown): string | null {
  const allowed = VOCABULARY[field];
  if (!allowed) return null;
  const values = Array.isArray(value) ? value : [value];
  for (const v of values) {
    if (!allowed.includes(String(v))) {
      return `'${String(v)}' is not a valid ${field}. Use one of: ${allowed.join(', ')}.`;
    }
  }
  return null;
}

/** Validate one already-structured selector object (the JSON path and edit mode). */
function validateObject(obj: unknown, index: number): { selector: ResourceSelector; error: null } | { selector: null; error: string } {
  const at = `Selector ${index + 1}`;
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return { selector: null, error: `${at}: must be an object like {"field": "domain", "op": "eq", "value": "finance"}.` };
  }
  const rec = obj as Record<string, unknown>;
  if (!('field' in rec)) {
    if (typeof rec.connector_id === 'string' && rec.connector_id) {
      // Documented shorthand: {"connector_id": "<uuid>"}. Store the explicit form.
      return { selector: { field: 'connector_id', op: 'eq', value: rec.connector_id }, error: null };
    }
    return { selector: null, error: `${at}: missing "field". Keys: ${KEYS_HINT}.` };
  }
  if (typeof rec.field !== 'string' || !rec.field.trim()) {
    return { selector: null, error: `${at}: "field" must be a non-empty string.` };
  }
  if (/[:=]/.test(rec.field)) {
    const [k, ...rest] = rec.field.split(/[:=]/);
    return {
      selector: null,
      error: `${at}: "${rec.field}" is a key:value pair, not a field. Write it as ${normalizeField(k)}:${rest.join(':').trim()} instead.`,
    };
  }
  const field = normalizeField(rec.field);
  if (!(SELECTOR_FIELDS as readonly string[]).includes(field)) {
    return { selector: null, error: `${at}: unknown field '${rec.field}'. Keys: ${KEYS_HINT}.` };
  }
  const op = (rec.op ?? 'eq') as SelectorOp;
  if (!SELECTOR_OPS.includes(op)) {
    return { selector: null, error: `${at}: unknown op '${String(rec.op)}'. Use one of: ${SELECTOR_OPS.join(', ')}.` };
  }
  if (!('value' in rec) || rec.value === '' || rec.value === null || rec.value === undefined) {
    return { selector: null, error: `${at}: missing "value".` };
  }
  const value = Array.isArray(rec.value) ? rec.value.map(String) : String(rec.value);
  const vocab = checkVocabulary(field, value);
  if (vocab) return { selector: null, error: `${at}: ${vocab}` };
  const selector: ResourceSelector = { field, op, value };
  if (Array.isArray(rec.actions)) selector.actions = rec.actions.map(String);
  return { selector, error: null };
}

function parsePair(token: string, index: number): { selector: ResourceSelector; error: null } | { selector: null; error: string } {
  const at = `Selector ${index + 1}`;
  const m = /^([A-Za-z_.]+)\s*[:=]\s*(.+)$/.exec(token);
  if (!m) {
    return {
      selector: null,
      error: `${at}: '${token}' is not a key:value pair. Write it as domain:${token.replace(/\s+/g, '-')} or use one of: ${KEYS_HINT}.`,
    };
  }
  const field = normalizeField(m[1]);
  const value = m[2].trim();
  if (!(SELECTOR_FIELDS as readonly string[]).includes(field)) {
    return { selector: null, error: `${at}: unknown key '${m[1]}'. Keys: ${KEYS_HINT}.` };
  }
  const vocab = checkVocabulary(field, value);
  if (vocab) return { selector: null, error: `${at}: ${vocab}` };
  return {
    selector: { field, op: LIST_FIELDS.has(field) ? 'contains' : 'eq', value },
    error: null,
  };
}

/**
 * Parse the Resource Selectors text into the objects the API stores.
 *
 * Empty input yields no selectors and no error (the form then requires the
 * "apply to all resources" opt-in, exactly like the API).
 */
export function parseResourceSelectors(input: string): ParsedSelectors {
  const text = input.trim();
  if (!text) return { selectors: [], error: null };

  if (text.startsWith('{') || text.startsWith('[')) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text.startsWith('[') ? text : `[${text}]`);
    } catch (e) {
      return {
        selectors: [],
        error: `Could not parse the selector JSON (${e instanceof Error ? e.message : 'invalid JSON'}). Either write key:value pairs such as domain:finance, or one JSON object per selector separated by commas.`,
      };
    }
    const items = Array.isArray(parsed) ? parsed : [parsed];
    const selectors: ResourceSelector[] = [];
    for (let i = 0; i < items.length; i++) {
      const r = validateObject(items[i], i);
      if (r.error !== null) return { selectors: [], error: r.error };
      selectors.push(r.selector);
    }
    return { selectors, error: null };
  }

  const tokens = text.split(/[,\n]/).map((t) => t.trim()).filter(Boolean);
  const selectors: ResourceSelector[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const r = parsePair(tokens[i], i);
    if (r.error !== null) return { selectors: [], error: r.error };
    selectors.push(r.selector);
  }
  return { selectors, error: null };
}

const OP_WORDS: Record<string, string> = {
  eq: '=',
  ne: '≠',
  in: 'in',
  contains: 'contains',
  gte: '≥',
  lte: '≤',
};

/** Human-readable label for a stored selector (policy cards, previews). */
export function formatSelectorLabel(input: Record<string, unknown> | ResourceSelector): string {
  const sel = input as Record<string, unknown>;
  if (!('field' in sel) && typeof sel.connector_id === 'string') {
    return `connector: ${sel.connector_id.slice(0, 8)}…`;
  }
  const field = typeof sel.field === 'string' ? sel.field.replace(/^resource\./, '') : '?';
  const op = typeof sel.op === 'string' ? sel.op : 'eq';
  const value = Array.isArray(sel.value) ? sel.value.join(', ') : String(sel.value ?? '');
  return `${field} ${OP_WORDS[op] ?? op} ${value}`;
}

function toShorthand(sel: Record<string, unknown>): string | null {
  if (!('field' in sel) && typeof sel.connector_id === 'string') return `connector_id:${sel.connector_id}`;
  if (typeof sel.field !== 'string' || typeof sel.value !== 'string') return null;
  if ('actions' in sel) return null;
  const field = normalizeField(sel.field);
  const op = sel.op ?? 'eq';
  const expected = LIST_FIELDS.has(field) ? 'contains' : 'eq';
  if (op !== expected) return null;
  if (/[,:\n]/.test(sel.value)) return null;
  return `${field}:${sel.value}`;
}

/**
 * Render stored selectors back into the form text (edit mode). Uses key:value
 * pairs when every selector can be expressed that way, JSON otherwise, so the
 * text always round-trips through `parseResourceSelectors` unchanged.
 */
export function selectorsToInput(selectors: Record<string, unknown>[]): string {
  if (!selectors || selectors.length === 0) return '';
  const short = selectors.map(toShorthand);
  if (short.every((s): s is string => s !== null)) return short.join(', ');
  return selectors.map((s) => JSON.stringify(s)).join(', ');
}
